<script setup lang="ts">
import {
  type CSSProperties,
  computed,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  shallowRef,
} from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { store } from '@/app/store';
import {
  portraitAspectRatioForOrientation,
  resolvePortraitOrientation,
} from '@/shared/lib/portrait-orientation';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import ArtStyleCarousel from './art-style/ArtStyleCarousel.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import ArtStyleVerticalGrid from './art-style/ArtStyleVerticalGrid.vue';
import type {
  ArtStyleGalleryItem,
  ArtStyleGridClass,
} from './art-style/artStyleTypes';
import {
  type ArtStyleFlow,
  useArtStyleFlow,
} from './art-style/useArtStyleFlow';

interface ArtStyleClassMap {
  'ArtStyle--horizontal-portraits': boolean | null;
  'ArtStyle--vertical-kiosk': boolean | null;
}

interface ArtStyleInlineStyle extends CSSProperties {
  '--ArtStyle--preview-aspect-ratio': number;
}

interface ArtStylePublicApi {
  _loadingIndicator: ArtStyleFlow['loadingIndicator']['value'];
  _navigationTimerId: ArtStyleFlow['navigationTimerId']['value'];
  _onKioskStyleInitialized: ArtStyleFlow['initializedHandler']['value'];
  _selectionLocked: boolean;
  _unmounted: boolean | undefined;
  readonly artStyleClasses: ArtStyleClassMap;
  readonly artStyleStyle: ArtStyleInlineStyle;
  readonly customGridClass: ArtStyleGridClass;
  galleryItems: ArtStyleGalleryItem[];
  generateGalleryItems: ArtStyleFlow['generateGalleryItems'];
  readonly gridRows: number | null;
  readonly hasSelectedStyle: boolean;
  readonly isVerticalKiosk: boolean | null;
  itemsPerSlide: number;
  navigateToPhotoStep: ArtStyleFlow['navigateToPhotoStep'];
  onGalleryItemClick: ArtStyleFlow['onGalleryItemClick'];
  onKioskStyleInitialize: ArtStyleFlow['onKioskStyleInitialize'];
  readonly portraitOrientation: ReturnType<typeof resolvePortraitOrientation>;
  shouldShowStyleSelector: ArtStyleFlow['shouldShowStyleSelector'];
  readonly stylePreviewAspectRatio: number;
  readonly ui: ArtStyleFlow['ui'];
}

const itemsPerSlide = shallowRef(3);
const isVerticalKiosk = computed(() => store.isVerticalKiosk);
const portraitOrientation = computed(() =>
  resolvePortraitOrientation(
    store.kiosk as Parameters<typeof resolvePortraitOrientation>[0]
  )
);
const stylePreviewAspectRatio = computed(() => {
  if (isVerticalKiosk.value && portraitOrientation.value === 'horizontal') {
    return portraitAspectRatioForOrientation(portraitOrientation.value);
  }
  return 3 / 4;
});
const artStyleClasses = computed<ArtStyleClassMap>(() => ({
  'ArtStyle--vertical-kiosk': isVerticalKiosk.value,
  'ArtStyle--horizontal-portraits':
    isVerticalKiosk.value && portraitOrientation.value === 'horizontal',
}));
const artStyleStyle = computed<ArtStyleInlineStyle>(() => ({
  '--ArtStyle--preview-aspect-ratio': stylePreviewAspectRatio.value,
}));

const flow = useArtStyleFlow({ stylePreviewAspectRatio });
const {
  customGridClass,
  enter,
  galleryItems,
  generateGalleryItems,
  gridRows,
  hasSelectedStyle,
  initializedHandler,
  isUnmounted,
  leave,
  loadingIndicator,
  navigateToPhotoStep,
  navigationTimerId,
  onGalleryItemClick,
  onKioskStyleInitialize,
  selectionLocked,
  shouldShowStyleSelector,
  ui,
}: ArtStyleFlow = flow;

const publicApi: ArtStylePublicApi = proxyRefs({
  _loadingIndicator: loadingIndicator,
  _navigationTimerId: navigationTimerId,
  _onKioskStyleInitialized: initializedHandler,
  _selectionLocked: selectionLocked,
  _unmounted: isUnmounted,
  get artStyleClasses(): ArtStyleClassMap {
    return artStyleClasses.value;
  },
  get artStyleStyle(): ArtStyleInlineStyle {
    return artStyleStyle.value;
  },
  get customGridClass(): ArtStyleGridClass {
    return customGridClass.value;
  },
  galleryItems,
  generateGalleryItems,
  get gridRows(): number | null {
    return gridRows.value;
  },
  get hasSelectedStyle(): boolean {
    return hasSelectedStyle.value;
  },
  get isVerticalKiosk(): boolean | null {
    return isVerticalKiosk.value;
  },
  itemsPerSlide,
  navigateToPhotoStep,
  onGalleryItemClick,
  onKioskStyleInitialize,
  get portraitOrientation(): ReturnType<typeof resolvePortraitOrientation> {
    return portraitOrientation.value;
  },
  shouldShowStyleSelector,
  get stylePreviewAspectRatio(): number {
    return stylePreviewAspectRatio.value;
  },
  ui,
});

onMounted((): void => {
  enter((): void => exposeToGlobalScope(publicApi, 'ArtStyle'));
});

onBeforeUnmount(leave);

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <div
      class="ArtStyle"
      data-testid="art-style-root"
      :class="artStyleClasses"
      :style="artStyleStyle"
    >
      <h1 class="ArtStyle__title kiosk-title" data-testid="art-style-title">
        {{ $t('ArtStyle.title') }}
      </h1>

      <ArtStyleVerticalGrid
        v-if="isVerticalKiosk"
        :grid-class="customGridClass"
        :items="galleryItems"
        :locked="hasSelectedStyle"
        @select="onGalleryItemClick"
      />

      <ArtStyleCarousel
        v-else
        :items="galleryItems"
        :items-per-slide="itemsPerSlide"
        :locked="hasSelectedStyle"
        @select="onGalleryItemClick"
      />
    </div>
  </KioskLayout>
</template>

<style scoped lang="scss">
  .ArtStyle {
    --ArtStyle--preview-aspect-ratio: calc(3 / 4);

    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &.ArtStyle--vertical-kiosk {
      --ArtStyle--available: calc(100vh - var(--Kiosk--wrapper-top-gap) - var(--Kiosk--wrapper-bottom-gap));
      --ArtStyle--title-space: 6rem;
      --ArtStyle--grid-available: calc(var(--ArtStyle--available) - var(--ArtStyle--title-space));

      height: var(--ArtStyle--available);
      max-height: var(--ArtStyle--available);
      overflow: hidden;
    }

    &.ArtStyle--horizontal-portraits {
      --ArtStyle--title-space: 4rem;
      --art-style-grid-rows-visible: 3;
      --art-style-grid-gap: 0.9rem;
      --art-style-grid-item-label-height: 1.5rem;
      --art-style-grid-shadow-padding: 0.35rem;
      --art-style-grid-item-max-width: calc(
        100vw
        - var(--Kiosk--wrapper-side-gap, 4rem)
        - var(--Kiosk--wrapper-side-gap, 4rem)
        - var(--art-style-grid-shadow-padding) * 2
      );
      --art-style-grid-width-columns: 1;
      --art-style-grid-width-gaps: 0;
      --art-style-grid-column-count: 1;
      --art-style-grid-special-column: auto;
      --art-style-grid-item-justify-self: center;
      --art-style-grid-title-margin-top: 0.35rem;
      --art-style-grid-title-font-size: 0.9rem;
    }
  }

  .ArtStyle__title {
    margin-bottom: 1rem;
    flex-shrink: 0;

    .ArtStyle--vertical-kiosk & {
      margin-top: 0;
      margin-bottom: 1rem;
    }

    .ArtStyle--horizontal-portraits & {
      margin-bottom: 0.5rem;
    }
  }
</style>
